import React from 'react';
import { Box, Paper, Typography, Chip, Stack, LinearProgress, Alert } from '@mui/material';
import { TrainingInsights, scoreColor, formatScore, barColor } from '../types';

interface Props {
  data: TrainingInsights['playbookCoverage'];
}

const PhaseBlock: React.FC<{
  title: string;
  score: number;
  coachingNote: string;
  coveredPoints: string[];
  missedPoints: string[];
}> = ({ title, score, coachingNote, coveredPoints, missedPoints }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Typography variant="subtitle2">{title}</Typography>
      <Chip label={`${score}%`} size="small" color={scoreColor(score)} />
    </Box>
    {coachingNote && (
      <Typography variant="body2" color="text.secondary" mb={1}>
        {coachingNote}
      </Typography>
    )}
    {coveredPoints?.length > 0 && (
      <Box mt={1}>
        <Typography variant="caption" color="success.main" fontWeight={600}>
          Covered ({coveredPoints.length})
        </Typography>
        <Typography variant="body2">{coveredPoints.join(' • ')}</Typography>
      </Box>
    )}
    {missedPoints?.length > 0 && (
      <Box mt={1}>
        <Typography variant="caption" color="error.main" fontWeight={600}>
          Missed ({missedPoints.length})
        </Typography>
        <Typography variant="body2">{missedPoints.join(' • ')}</Typography>
      </Box>
    )}
  </Paper>
);

const PlaybookCoverageSection: React.FC<Props> = ({ data }) => {
  if (!data) return null;
  if (!data.wasUsed) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
        <Typography variant="h6" gutterBottom>Playbook Coverage</Typography>
        <Alert severity="info" variant="outlined">
          This training track did not use a sales playbook. Enable a playbook
          on the track to see intro / qualifying / deal-progression coverage.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Playbook Coverage</Typography>
        <Chip
          label={formatScore(data.overallScore)}
          color={scoreColor(data.overallScore)}
          size="medium"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {data.overallSummary}
      </Typography>
      {data.overallScore != null && (
        <LinearProgress
          variant="determinate"
          value={data.overallScore}
          color={barColor(data.overallScore)}
          sx={{ mb: 3, height: 8, borderRadius: 1 }}
        />
      )}

      <Stack spacing={2}>
        {data.introCoverage && (
          <PhaseBlock
            title="Intro / Pitch Coverage"
            score={data.introCoverage.score}
            coachingNote={data.introCoverage.coachingNote}
            coveredPoints={data.introCoverage.coveredPoints}
            missedPoints={data.introCoverage.missedPoints}
          />
        )}
        {data.questionCoverage && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">Qualifying Questions</Typography>
              <Chip
                label={`${data.questionCoverage.score}%`}
                size="small"
                color={scoreColor(data.questionCoverage.score)}
              />
            </Box>
            {data.questionCoverage.coachingNote && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {data.questionCoverage.coachingNote}
              </Typography>
            )}
            {data.questionCoverage.tagsCovered?.length > 0 && (
              <Box mt={1}>
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  Tags covered
                </Typography>
                <Typography variant="body2">
                  {data.questionCoverage.tagsCovered.join(', ')}
                </Typography>
              </Box>
            )}
            {data.questionCoverage.tagsMissed?.length > 0 && (
              <Box mt={1}>
                <Typography variant="caption" color="error.main" fontWeight={600}>
                  Tags missed
                </Typography>
                <Typography variant="body2">
                  {data.questionCoverage.tagsMissed.join(', ')}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
        {data.dealProgressionCoverage && (
          <PhaseBlock
            title="Deal Progression / Closing"
            score={data.dealProgressionCoverage.score}
            coachingNote={data.dealProgressionCoverage.coachingNote}
            coveredPoints={data.dealProgressionCoverage.coveredPoints}
            missedPoints={data.dealProgressionCoverage.missedPoints}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default PlaybookCoverageSection;
